import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, DollarSign, UserPlus, X, FileText, Fingerprint, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import fingerprintApi, { FingerprintDevice } from '../services/fingerprintApi';
import FingerprintEnrollmentModal from '../components/FingerprintEnrollmentModal';
import FingerprintVerificationModal from '../components/FingerprintVerificationModal';
import toast from 'react-hot-toast';

interface Employee {
  id: number;
  userId: number;
  type: 'FIXED' | 'METER';
  rate: number;
  designation?: string | null;
  canApproveAttendance?: boolean;
  hasFingerprint?: boolean;
  fingerprintId?: number | null;
  fingerprintEnrolledAt?: string | null;
  user: {
    id: number;
    name: string;
    email: string;
    phone?: string | null;
    role: string;
    roleId: number | null;
    userRole?: {
      id: number;
      name: string;
      description: string;
    };
  };
  createdAt: string;
  updatedAt: string;
}

interface EmployeeForm {
  name: string;
  email: string;
  phone?: string;
  password: string;
  roleId: number | null;
  type: 'FIXED' | 'METER';
  rate: number;
  designation?: string;
  canApproveAttendance?: boolean;
}

interface Role {
  id: number;
  name: string;
  description: string;
}

const Employees: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [deletingEmployee, setDeletingEmployee] = useState<Employee | null>(null);
  const [advanceModalOpen, setAdvanceModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [enrollmentModalOpen, setEnrollmentModalOpen] = useState(false);
  const [fingerprintEmployee, setFingerprintEmployee] = useState<Employee | null>(null);
  const [deletingFingerprintEmployee, setDeletingFingerprintEmployee] = useState<Employee | null>(null);
  const [deleteDeviceId, setDeleteDeviceId] = useState<number | null>(null);

  // Get available devices for delete operation
  const { data: deleteDevicesData } = useQuery({
    queryKey: ['fingerprint-devices-my'],
    queryFn: () => fingerprintApi.getMyFingerprintDevices(),
    enabled: !!deletingFingerprintEmployee
  });

  const deleteDevices: FingerprintDevice[] = deleteDevicesData?.devices || [];
  const [verifyingEmployee, setVerifyingEmployee] = useState<Employee | null>(null);
  const queryClient = useQueryClient();

  // Check if fingerprint module is enabled
  const { data: fingerprintEnabled } = useQuery({
    queryKey: ['fingerprint-enabled'],
    queryFn: () => fingerprintApi.checkFingerprintEnabled()
  });

  const isFingerprintEnabled = fingerprintEnabled?.enabled || false;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EmployeeForm>();

  const {
    register: registerAdvance,
    handleSubmit: handleAdvanceSubmit,
    reset: resetAdvance,
    formState: { errors: advanceErrors },
  } = useForm<{ amount: number; reason: string }>();

  // Fetch employees
  const { data: employeesResponse, isLoading, error } = useQuery({
    queryKey: ['employees'],
    queryFn: () => apiService.get('/employees'),
  });
  
  const employees = employeesResponse?.employees || [];

  // Fetch roles
  const { data: rolesResponse } = useQuery({
    queryKey: ['roles'],
    queryFn: () => apiService.get('/roles'),
  });

  React.useEffect(() => {
    if (rolesResponse?.data) {
      setRoles(rolesResponse.data);
    }
  }, [rolesResponse]);

  // Create employee mutation
  const createMutation = useMutation({
    mutationFn: (data: EmployeeForm) => apiService.post('/employees', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      toast.success('Employee created successfully');
      setIsModalOpen(false);
      reset();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create employee');
    },
  });

  // Update employee mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<EmployeeForm> }) =>
      apiService.put(`/employees/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      toast.success('Employee updated successfully');
      setIsModalOpen(false);
      setEditingEmployee(null);
      reset();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update employee');
    },
  });

  // Delete employee mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.delete(`/employees/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      toast.success('Employee deleted successfully');
      setDeletingEmployee(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete employee');
    },
  });

  // Add advance mutation
  const addAdvanceMutation = useMutation({
    mutationFn: ({ employeeId, amount, reason }: { employeeId: number; amount: number; reason: string }) =>
      apiService.post(`/employees/${employeeId}/advances`, { amount, reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      toast.success('Advance added successfully');
      setAdvanceModalOpen(false);
      resetAdvance();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to add advance');
    },
  });

  // Delete fingerprint mutation
  const deleteFingerprintMutation = useMutation({
    mutationFn: ({ employeeId, deviceId }: { employeeId: number; deviceId: number }) => 
      fingerprintApi.deleteFingerprint(employeeId, { deviceId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      toast.success('Fingerprint deleted successfully');
      setDeletingFingerprintEmployee(null);
      setDeleteDeviceId(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete fingerprint');
    },
  });

  const onSubmit = (data: EmployeeForm) => {
    if (editingEmployee) {
      updateMutation.mutate({ id: editingEmployee.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const onAdvanceSubmit = (data: { amount: number; reason: string }) => {
    if (selectedEmployee) {
      addAdvanceMutation.mutate({
        employeeId: selectedEmployee.id,
        amount: data.amount,
        reason: data.reason,
      });
    }
  };

  const handleEdit = (employee: Employee) => {
    setEditingEmployee(employee);
    reset({
      name: employee.user.name,
      email: employee.user.email,
      phone: employee.user.phone || '',
      roleId: employee.user.roleId,
      type: employee.type,
      rate: employee.rate,
      designation: employee.designation || '',
      canApproveAttendance: !!employee.canApproveAttendance,
    });
    setIsModalOpen(true);
  };

  const handleDelete = (employee: Employee) => {
    setDeletingEmployee(employee);
  };

  const handleAddAdvance = (employee: Employee) => {
    setSelectedEmployee(employee);
    resetAdvance();
    setAdvanceModalOpen(true);
  };

  const handleViewReport = (employee: Employee) => {
    // Navigate to reports page with employee filter
    navigate(`/employee-report?employeeId=${employee.id}`);
  };

  const handleEnrollFingerprint = (employee: Employee) => {
    setFingerprintEmployee(employee);
    setEnrollmentModalOpen(true);
  };

  const handleDeleteFingerprint = (employee: Employee) => {
    setDeletingFingerprintEmployee(employee);
  };

  const handleVerifyFingerprint = (employee: Employee) => {
    setVerifyingEmployee(employee);
  };

  const confirmDeleteFingerprint = () => {
    if (deletingFingerprintEmployee && deleteDeviceId) {
      deleteFingerprintMutation.mutate({ 
        employeeId: deletingFingerprintEmployee.id, 
        deviceId: deleteDeviceId 
      });
    } else if (!deleteDeviceId) {
      toast.error('Please select a device');
    }
  };

  const confirmDelete = () => {
    if (deletingEmployee) {
      deleteMutation.mutate(deletingEmployee.id);
    }
  };

  const openModal = () => {
    setEditingEmployee(null);
    reset({
      name: '',
      email: '',
      phone: '',
      password: '',
      roleId: null,
      type: 'FIXED',
      rate: 0,
      designation: '',
      canApproveAttendance: false,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingEmployee(null);
    reset();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load employees. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Floating Add Employee button */}
      <div className="fixed top-6 right-6 z-[1002]">
        <button
          type="button"
          onClick={openModal}
          className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white shadow-xl hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 hover:scale-105"
          title="Add Employee"
        >
          <Plus className="h-6 w-6" />
        </button>
      </div>


      {/* Employees Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {employees.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No employees found. Add your first employee to get started.</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {employees.map((employee: Employee) => (
              <li key={employee.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-primary-600 font-medium text-sm">
                          {employee.user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">{employee.user.name}</div>
                      <div className="text-sm text-gray-500">{employee.user.email}</div>
                      <div className="text-sm text-gray-500">
                        {employee.type === 'FIXED' ? 'Fixed Salary' : 'Meter-based'} - ₹{employee.rate}
                        {employee.type === 'METER' ? '/meter' : '/month'}
                      </div>
                      {employee.user.userRole && (
                        <div className="text-xs text-blue-600 font-medium">
                          Role: {employee.user.userRole.name}
                        </div>
                      )}
                      {isFingerprintEnabled && (
                        <div className="mt-1">
                          {employee.hasFingerprint ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              <Fingerprint className="w-3 h-3 mr-1" />
                              Fingerprint Enrolled
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                              Not Enrolled
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {isFingerprintEnabled && (
                      <>
                        {employee.hasFingerprint ? (
                          <>
                            <button
                              onClick={() => handleVerifyFingerprint(employee)}
                              className="text-green-600 hover:text-green-900"
                              title="Verify Fingerprint"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteFingerprint(employee)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Fingerprint"
                            >
                              <Fingerprint className="h-4 w-4" />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleEnrollFingerprint(employee)}
                            className="text-primary-600 hover:text-primary-900"
                            title="Enroll Fingerprint"
                          >
                            <Fingerprint className="h-4 w-4" />
                          </button>
                        )}
                      </>
                    )}
                    <button
                      onClick={() => handleViewReport(employee)}
                      className="text-blue-600 hover:text-blue-900"
                      title="View Report"
                    >
                      <FileText className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleAddAdvance(employee)}
                      className="text-green-600 hover:text-green-900"
                      title="Add Advance"
                    >
                      <DollarSign className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(employee)}
                      className="text-primary-600 hover:text-primary-900"
                      title="Edit"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(employee)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Add/Edit Employee Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                    <input
                      {...register('name', { required: 'Name is required' })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter employee name"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      {...register('email', { 
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address'
                        }
                      })}
                      type="email"
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter email address"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <input
                      {...register('phone')}
                      type="tel"
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter phone number (used for login)"
                    />
                    {errors.phone && (
                      <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                    )}
                  </div>
                </div>

                {!editingEmployee && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                    <input
                      {...register('password', { 
                        required: 'Password is required',
                        minLength: { value: 6, message: 'Password must be at least 6 characters' }
                      })}
                      type="password"
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter password"
                    />
                    {errors.password && (
                      <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                    <select
                      {...register('roleId', { required: 'Role is required' })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select a role</option>
                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name} - {role.description}
                        </option>
                      ))}
                    </select>
                    {errors.roleId && (
                      <p className="mt-1 text-sm text-red-600">{errors.roleId.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Salary Type</label>
                    <select
                      {...register('type', { required: 'Salary type is required' })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="FIXED">Fixed Salary</option>
                      <option value="METER">Meter-based</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Rate</label>
                  <input
                    {...register('rate', { 
                      required: 'Rate is required',
                      min: { value: 0, message: 'Rate must be positive' }
                    })}
                    type="number"
                    step="0.01"
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter rate"
                  />
                  {errors.rate && (
                    <p className="mt-1 text-sm text-red-600">{errors.rate.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Designation (optional)</label>
                  <input
                    {...register('designation')}
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g. Karigar, Master"
                  />
                  <p className="mt-1 text-xs text-gray-500">Free-text job role shown on attendance screens.</p>
                </div>

                <div className="flex items-start gap-3">
                  <input
                    id="canApproveAttendance"
                    type="checkbox"
                    {...register('canApproveAttendance')}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="canApproveAttendance" className="text-sm text-gray-700">
                    <span className="font-medium">Can approve attendance</span>
                    <span className="block text-xs text-gray-500">Trusted manager — may review and approve other employees' clock-ins.</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending || updateMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Advance Modal */}
      {advanceModalOpen && selectedEmployee && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Add Advance for {selectedEmployee.user.name}
              </h3>
              <form onSubmit={handleAdvanceSubmit(onAdvanceSubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Amount</label>
                  <input
                    {...registerAdvance('amount', { 
                      required: 'Amount is required',
                      min: { value: 0.01, message: 'Amount must be greater than 0' }
                    })}
                    type="number"
                    step="0.01"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Enter advance amount"
                  />
                  {advanceErrors.amount && (
                    <p className="mt-1 text-sm text-red-600">{advanceErrors.amount.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Reason</label>
                  <textarea
                    {...registerAdvance('reason', { required: 'Reason is required' })}
                    rows={3}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Enter reason for advance"
                  />
                  {advanceErrors.reason && (
                    <p className="mt-1 text-sm text-red-600">{advanceErrors.reason.message}</p>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setAdvanceModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addAdvanceMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
                  >
                    {addAdvanceMutation.isPending ? 'Adding...' : 'Add Advance'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingEmployee && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Employee</h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to delete "{deletingEmployee.user.name}"? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeletingEmployee(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleteMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fingerprint Enrollment Modal */}
      {enrollmentModalOpen && fingerprintEmployee && (
        <FingerprintEnrollmentModal
          employee={{
            id: fingerprintEmployee.id,
            user: {
              name: fingerprintEmployee.user.name,
              email: fingerprintEmployee.user.email
            }
          }}
          onClose={() => {
            setEnrollmentModalOpen(false);
            setFingerprintEmployee(null);
            queryClient.invalidateQueries({ queryKey: ['employees'] });
          }}
        />
      )}

      {/* Delete Fingerprint Confirmation Modal */}
      {deletingFingerprintEmployee && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Fingerprint</h3>
              <p className="text-sm text-gray-500 mb-4">
                Are you sure you want to delete the fingerprint for {deletingFingerprintEmployee.user.name}?
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Device:
                </label>
                <select
                  value={deleteDeviceId || ''}
                  onChange={(e) => setDeleteDeviceId(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">-- Select Device --</option>
                  {deleteDevices.map((device) => (
                    <option key={device.id} value={device.deviceId}>
                      {device.name} (ID: {device.deviceId})
                    </option>
                  ))}
                </select>
                {deleteDevices.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">No devices available.</p>
                )}
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setDeletingFingerprintEmployee(null);
                    setDeleteDeviceId(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteFingerprint}
                  disabled={deleteFingerprintMutation.isPending || !deleteDeviceId}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteFingerprintMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Socket-based Fingerprint Verification Modal */}
      {verifyingEmployee && (
        <FingerprintVerificationModal
          employee={{
            id: verifyingEmployee.id,
            user: {
              name: verifyingEmployee.user.name,
              email: verifyingEmployee.user.email
            }
          }}
          onClose={() => {
            setVerifyingEmployee(null);
            queryClient.invalidateQueries({ queryKey: ['employees'] });
          }}
          onVerifySuccess={(employee) => {
            setVerifyingEmployee(null);
            queryClient.invalidateQueries({ queryKey: ['employees'] });
            toast.success(`Fingerprint verified successfully for ${employee.user.name}!`);
          }}
        />
      )}
    </div>
  );
};

export default Employees;